import React, { useEffect } from 'react';
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { BrowserRouter as Router } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { useAppDispatch } from './store/hooks';
import { setTokens, setBootstrapStatus, setAuthenticatedResolved, signOut } from './store/slices/authSlice';

import AppRoutes from './components/app_routes'
import ServiceWorkerNavBridge from './components/ServiceWorkerNavBridge';
import ChatToastListener from './components/ChatToastListener';
import PushResyncListener from './components/PushResyncListener';
import { BadgeRevealHost } from './features/badges/reveal';
import { ConfirmProvider } from './ui/components/ConfirmDialog';
import { SessionManager } from './components/authentication/SessionManager';
import ContributorPendingAdminNotifier from './components/notifications/ContributorPendingAdminNotifier';
import InstallBanner from './components/pwa/InstallBanner';
import { getOidcConfiguration } from './services/oidcDiscovery';
import { migrateTokensFromLocalStorage, getAccessToken } from './utils/authTokens';
import { tokenService } from './services/tokenService';
import { clearLoginAttempts } from './utils/authLoopGuard';
import { SERVER_URL } from './config/env';

const GlobalStyle = createGlobalStyle`
  * {
    ${'' /* font-family: 'Fira Mono', monospace; */}
    font-family: 'JetBrains Mono', monospace;
    ${'' /* font-family: 'Roboto Mono', monospace; */}
    ${'' /* font-family: 'Source Code Pro', monospace; */}
    ${'' /* font-weight: 400; */}
  }
`;


function App() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Pre-fetch OIDC configuration on app startup
    getOidcConfiguration().catch(error => {
      console.error('[App] Failed to fetch OIDC configuration:', error);
      // Fallback to hardcoded endpoints will be used
    });

    const backendAuthBase = SERVER_URL ? `${SERVER_URL}/api/auth` : '/api/auth';

    const bootstrapFromBffSession = async () => {
      const response = await fetch(`${backendAuthBase}/session`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data?.transient && !data?.authenticated) {
        console.warn('[App] BFF session bootstrap: auth temporarily unavailable');
        return 'transient';
      }
      if (!data?.authenticated || !data?.accessToken) {
        return false;
      }

      dispatch(setTokens({
        accessToken: data.accessToken,
      }));
      // A session that survives a page load is real progress, unlike a bare code
      // exchange (which succeeds on every lap of a silent re-auth loop). This is
      // the only place the loop counter should be cleared.
      clearLoginAttempts();
      console.log('[App] Session restored via backend BFF session endpoint');
      return true;
    };

    // Bootstrap auth on app startup
    const bootstrapAuth = async () => {
      dispatch(setBootstrapStatus('loading'));
      dispatch(setAuthenticatedResolved(false));

      // Migrate tokens from localStorage (one-time)
      migrateTokensFromLocalStorage();

      try {
        const accessToken = getAccessToken();

        // if we have a valid access token, hydrate redux immediately
        if (accessToken && !tokenService.isTokenExpired(accessToken, 0)) {
          dispatch(setTokens({ accessToken }));
          clearLoginAttempts();
          console.log('[App] Access token still valid, using existing token');
          return;
        }

        let restored = false;
        try {
          restored = await bootstrapFromBffSession();
        } catch (error) {
          console.warn('[App] BFF session bootstrap failed:', error);
        }

        if (restored === 'transient') {
          console.log('[App] Session not restored (transient); BFF cookie kept');
        } else if (!restored) {
          dispatch(signOut());
          console.log('[App] No active session restored');
        }
      } catch (error) {
        console.log('[App] Bootstrap auth failed:', error);
      } finally {
        dispatch(setBootstrapStatus('ready'));
        dispatch(setAuthenticatedResolved(true));
      }
    };

    bootstrapAuth();
  }, [dispatch]);

  return (
    <Router>
      <ServiceWorkerNavBridge />
      <ChatToastListener />
      <PushResyncListener />
      <SessionManager />
      <ContributorPendingAdminNotifier />
      {/* Toasts live bottom-left, stacked upward above the footer. The stack is
          pinned there in CSS (index.css, ".Toastify" block) so per-call
          `position` overrides scattered across the app can't drag a toast back
          over the header nav. */}
      <ToastContainer
        position="bottom-left"
        autoClose={4500}
        closeOnClick
        closeButton
        draggable={true}
        draggablePercent={30}
        pauseOnHover
        pauseOnFocusLoss
      />
      <GlobalStyle />
      <ConfirmProvider>
        <div className="App">
          <AppRoutes />
          <BadgeRevealHost />
          <InstallBanner />
        </div>
      </ConfirmProvider>
    </Router>
  );
}


export default App;
